import { useMemo } from 'react'
import { Row } from 'react-table'
import { useIntl } from 'react-intl'
import ReactPaginate from 'react-paginate'
import { mdiChevronLeft } from '@mdi/js'
import { mdiChevronRight } from '@mdi/js'
import { mdiSkipNext } from '@mdi/js'
import { mdiSkipPrevious } from '@mdi/js'

import { DropdownOption } from 'components/Input/Dropdown'
import { paginationMessages } from './messages'
import { ROWS_PER_PAGE_OPTIONS } from '../consts'
import {
  MobilePaginationControlWrapper,
  MobilePaginationControl,
  MobilePaginationCurrentPage,
  PaginationContainer,
  PaginationControlWrapper,
  PaginationCurrentPage,
  PaginationIcon,
  PaginationRowsPerPage,
  ReactPaginateWrapper,
} from './styles'

interface PaginationProps<T extends Object> {
  gotoPage: (index: number) => void
  canPreviousPage: (index: number) => void
  canNextPage: (index: number) => void
  previousPage: () => void
  nextPage: () => void
  pageOptions?: Row<T>[]
  pageCount: number
  setPageSize: (index: number) => void
  pageIndex: number
  pageSize: number
  itemCount?: number
  rowsLength: number
  width?: number
}
const RowsPerPageOptions = ROWS_PER_PAGE_OPTIONS.map((option) => ({
  label: `${option}`,
  value: option,
}))

function Pagination<T>({
  gotoPage,
  canPreviousPage,
  canNextPage,
  previousPage,
  nextPage,
  pageOptions,
  pageCount,
  setPageSize,
  pageIndex,
  pageSize,
  itemCount,
  rowsLength,
  width,
}: PaginationProps<T>) {
  const { formatMessage } = useIntl()

  const handlePageClick = (event: { selected: number }) => {
    gotoPage(event.selected)
  }

  const value = useMemo(
    () =>
      RowsPerPageOptions.find((option) => option.value === pageSize) || {
        label: `${pageSize}`,
        value: pageSize,
      },
    [pageSize]
  )

  return (
    <PaginationContainer $width={width}>
      <PaginationControlWrapper>
        <PaginationRowsPerPage
          name={formatMessage(paginationMessages.rowsPerPageSelectorName)}
          value={value}
          onChange={(option: DropdownOption) =>
            setPageSize(Number(option.value))
          }
          options={RowsPerPageOptions}
          menuPlacement="top"
        />
        <PaginationCurrentPage>
          <span>
            /<b> {itemCount ? itemCount : rowsLength}</b>
          </span>
        </PaginationCurrentPage>
      </PaginationControlWrapper>

      <ReactPaginateWrapper>
        <ReactPaginate
          breakLabel="..."
          nextLabel={<PaginationIcon path={mdiChevronRight} />}
          onPageChange={(event) => handlePageClick(event)}
          pageRangeDisplayed={3}
          marginPagesDisplayed={1}
          pageCount={pageCount}
          previousLabel={<PaginationIcon path={mdiChevronLeft} />}
          renderOnZeroPageCount={() => null}
          forcePage={pageIndex}
        />
      </ReactPaginateWrapper>

      <MobilePaginationControlWrapper>
        <MobilePaginationControl
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
        >
          <PaginationIcon path={mdiSkipPrevious} disabled={!canPreviousPage} />
        </MobilePaginationControl>
        <MobilePaginationControl
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
        >
          <PaginationIcon path={mdiChevronLeft} disabled={!canPreviousPage} />
        </MobilePaginationControl>
        <MobilePaginationCurrentPage>
          {pageIndex + 1}
        </MobilePaginationCurrentPage>
        <MobilePaginationControl
          onClick={() => nextPage()}
          disabled={!canNextPage}
        >
          <PaginationIcon path={mdiChevronRight} disabled={!canNextPage} />
        </MobilePaginationControl>
        <MobilePaginationControl
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
        >
          <PaginationIcon path={mdiSkipNext} disabled={!canNextPage} />
        </MobilePaginationControl>
      </MobilePaginationControlWrapper>
    </PaginationContainer>
  )
}

export default Pagination
